import { BuildStatus } from '@cadstrom/cadstrom-shared';
import { RequirementResultType } from '@cadstrom/sigma-engine-shared';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect } from 'react';
import * as z from 'zod';

import { useTRPC } from '#src/trpc.js';
import { captureAnalyticsEvent } from '#utils/analytics/analytics-events.js';
import { displayedRevisionStatus } from '#utils/revision-display-status.js';
import { revisionQueryOptions } from '#utils/revision-query-options.js';
import { enabledAnalysisTypes } from './analysis-types.js';
import { AnalysisResultsContent } from './components/analysis-results-content.js';
import { ErrorState } from './components/error-state.js';
import { useResultsOpenedBeacon } from './use-results-opened-beacon.js';

// An indeterminate status carries nothing the page can present, so it surfaces the error
// screen rather than spinning on progress that will never resolve. An errored revision is
// not in this set: it presents as the status it failed from.
const RESULTS_UNAVAILABLE_STATUSES = new Set<string>([BuildStatus.Unknown]);

export const Route = createFileRoute(
	'/_authorization-gate/orgs/$orgName/projects/$projectId/revisions/$revisionId/results',
)({
	validateSearch: z.object({
		types: z.array(z.nativeEnum(RequirementResultType)).catch(enabledAnalysisTypes).default(enabledAnalysisTypes),
		resultId: z.string().optional(),
		findingId: z.string().optional(),
		// Attribution token carried by the results-ready email link; declared so the
		// router keeps it, and reported by the results-opened beacon.
		ref: z.string().optional(),
		// Search values arrive as strings, so coerce 'true'/'false' before boolean validation
		// (otherwise `?reqv1=true` fails `z.boolean()` and silently falls back to V2).
		reqv1: z.preprocess(
			(value) =>
				value === 'true' ? true
				: value === 'false' ? false
				: value,
			z.boolean().optional().catch(false).default(false),
		),
		tab: z.preprocess(
			(value) => (value === 'chat' ? 'workbench' : value),
			z
				.enum(['analysis-results', 'ai-findings', 'v2-findings', 'v2-results', 'workbench'])
				.catch('analysis-results')
				.default('analysis-results'),
		),
		leftTab: z.enum(['design-explorer', 'bom', 'block-diagram']).catch('design-explorer').default('design-explorer'),
		v2SelectedId: z.string().optional(),
		v2FindingSelectedId: z.string().optional(),
		v2ArGroupSelectedId: z.string().optional(),
		v2ArGroupSelectedIds: z.array(z.string()).optional(),
		v2FindingFilters: z.string().optional(),
		v2FindingSearch: z.string().optional(),
		v2ResultFilters: z.string().optional(),
		v2ResultSearch: z.string().optional(),
		v2FindingGroup: z.string().optional(),
		v2FindingSort: z.string().optional(),
		v2ResultGroup: z.string().optional(),
		v2ResultSort: z.string().optional(),
	}),
	staticData: {
		authorize: (ability) => ability.can('view', 'analysisResult'),
		navigation: {
			label: 'Results',
			keywords: ['validation', 'requirements', 'tests', 'bom', 'bill of materials', 'parts'],
		},
	},
	loader: async ({ context: { queryClient, getProjectPreviewOptions } }) => {
		const { projectPreview } = await queryClient.ensureQueryData(getProjectPreviewOptions);
		return { projectPreview };
	},
	head: ({ loaderData }) => ({
		meta: [
			{
				title: (loaderData ? loaderData.projectPreview.name : 'Project') + ' • Results • Cadstrom',
			},
		],
	}),
	component: AnalysisResultsPage,
});

function AnalysisResultsPage() {
	const { projectId, revisionId } = Route.useParams();
	// Polls until the build settles, so the page comes alive (tabs enable, BOM flips to the review
	// flow) without a manual refresh.
	const trpc = useTRPC();

	const { data: revisionData } = useSuspenseQuery(revisionQueryOptions(trpc, revisionId));
	const ref = Route.useSearch({ select: (search) => search.ref });
	useResultsOpenedBeacon({ revisionId, ref });

	const revisionStatus = revisionData.revision.status;
	const statusMessage = revisionData.revision.statusMessage;
	// The failure is staff's news, not the customer's: the page keeps the layout and wording
	// of the status the revision failed from.
	const displayedStatus = displayedRevisionStatus(revisionData.revision);

	useEffect(() => {
		if (!RESULTS_UNAVAILABLE_STATUSES.has(revisionStatus)) {
			return;
		}
		captureAnalyticsEvent('results_unavailable', {
			project_id: projectId,
			revision_id: revisionId,
			revision_status: revisionStatus,
			reason: 'error',
		});
	}, [projectId, revisionId, revisionStatus]);

	if (RESULTS_UNAVAILABLE_STATUSES.has(revisionStatus)) {
		return <ErrorState errorMessage={statusMessage} />;
	}

	return (
		<AnalysisResultsContent
			projectId={projectId}
			buildId={revisionId}
			revisionStatus={displayedStatus}
			revisionHasFailed={revisionStatus === BuildStatus.Error}
			revisionAutomationMode={revisionData.revision.automationMode}
		/>
	);
}
